/**
 * GeneratorsMap Component
 * Displays generators on an interactive map using Leaflet
 */

import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Spin } from 'antd';
import { useGeneratorsMap } from '@/hooks/useGenerators';
import { GeneratorPopup } from './GeneratorPopup';
import type { GeneratorStatus } from '@/api/types/generators.types';
import './GeneratorsMap.css';

// Fix Leaflet default icon issue in Webpack/Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Status colors matching popup
const statusColors = {
  available: '#52c41a',
  rented: '#1890ff',
  maintenance: '#faad14',
  broken: '#ff4d4f',
  decommissioned: '#8c8c8c',
};

const statusLabels = {
  available: 'Dostupan',
  rented: 'Iznajmljen',
  maintenance: 'Na održavanju',
  broken: 'Pokvaren',
  decommissioned: 'Van upotrebe',
};

// Create custom colored marker icons
const createMarkerIcon = (status: GeneratorStatus) => {
  const color = statusColors[status];
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <path fill="${color}" stroke="#fff" stroke-width="1.5"
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    className: 'custom-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

export const GeneratorsMap: React.FC = () => {
  const { data: generators, isLoading, error } = useGeneratorsMap();

  // Calculate map center based on average of all generator coordinates
  const mapCenter = useMemo(() => {
    if (!generators || generators.length === 0) {
      return [44.7866, 20.4489] as [number, number]; // Default: Belgrade
    }

    const avgLat =
      generators.reduce((sum, g) => sum + g.gps_lat, 0) / generators.length;
    const avgLon =
      generators.reduce((sum, g) => sum + g.gps_lon, 0) / generators.length;

    return [avgLat, avgLon] as [number, number];
  }, [generators]);

  if (isLoading) {
    return (
      <div className="generators-map-loading">
        <Spin size="large" />
        <p>Učitavanje mape...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="generators-map-error">
        Greška pri učitavanju generatora: {error.message}
      </div>
    );
  }

  if (!generators || generators.length === 0) {
    return (
      <div className="generators-map-empty">
        Nema generatora za prikaz na mapi.
      </div>
    );
  }

  return (
    <div className="generators-map-container">
      <MapContainer
        center={mapCenter}
        zoom={8}
        className="generators-map"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {generators.map((generator) => (
          <Marker
            key={generator.id}
            position={[generator.gps_lat, generator.gps_lon]}
            icon={createMarkerIcon(generator.status)}
          >
            <Popup>
              <GeneratorPopup generator={generator} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map Legend */}
      <div className="generators-map-legend">
        <h4>Legenda</h4>
        {Object.entries(statusColors).map(([status, color]) => (
          <div key={status} className="legend-item">
            <span
              className="legend-marker"
              style={{ backgroundColor: color }}
            ></span>
            <span className="legend-label">
              {statusLabels[status as GeneratorStatus]}
            </span>
          </div>
        ))}
        <div className="legend-count">
          Ukupno: {generators.length}{' '}
          {generators.length === 1 ? 'generator' : 'generatora'}
        </div>
      </div>
    </div>
  );
};
